import { Message } from "@cnx/common";
import { Box, Center, Flex, Loader, Stack } from "@mantine/core";
import ChatInput from "components/ChatInput";
import { useChatRoomSocket } from "hooks/useChatRoomSocket";
import { useMessages } from "hooks/useMessages";
import { useReadMessagesSocket } from "hooks/useReadMessagesSocket";

import { formatChatTime } from "utils/chat";
import { useCreateDiscussion } from "./hooks/useCreateDiscussion";
import { useFirstUnreadMessage } from "./hooks/useFirstUnreadMessage";
import { useRedirectWhenDiscussionGone } from "./hooks/useRedirectWhenDiscussionGone";
import { useChatContextStore } from "./providers/ChatContextProvider";
import ChatMessageList from "./ui/ChatMessageList";
import MessageAttachment from "./ui/MessageAttachment";
import UserMessage from "./ui/UserMessage";

export const ChatDiscussionView = ({
  header,
  popover,
}: {
  header?: React.ReactNode;
  popover?: React.ReactNode;
}) => {
  const {
    discussionId,
    discussion,
    discussionIsPending,
    contextId,
    discussionContextType,
    editingMessage,
    setEditingMessage,
    selectedMessages,
    setSelectedMessages,
  } = useChatContextStore();

  const { onDiscussionCreated } = useCreateDiscussion();

  const { messages, isPending: messagesIsLoading } = useMessages({
    discussionId,
  });

  const { firstUnreadMessageId, setFirstUnreadMessageId } =
    useFirstUnreadMessage(messages);

  useChatRoomSocket(discussionId);
  useReadMessagesSocket({
    messages,
    discussionId,
    context: discussion?.context,
  });

  useRedirectWhenDiscussionGone();

  const hasDraftContext =
    !discussionId && !!discussionContextType && !!contextId;

  const showMessagesLoader =
    messagesIsLoading && !!discussionId && !hasDraftContext;

  if (discussionId && !discussion && !hasDraftContext && !discussionIsPending)
    return null;
  if (!discussionId && !hasDraftContext) return null;

  const handleCancelEdit = () => setEditingMessage(null);

  const handleToggleSelect = (message: Message) => {
    setSelectedMessages(prev => {
      const exists = prev.some(m => m.id === message.id);
      return exists
        ? prev.filter(m => m.id !== message.id)
        : [...prev, message];
    });
  };

  return (
    <Flex flex={1} direction={"column"} justify={"space-between"} mih={0}>
      <Stack flex={1} gap={0} mih={0}>
        {header}

        {popover && (
          <Center py={10} px={20}>
            <Center w={"100%"} maw={800}>
              {popover}
            </Center>
          </Center>
        )}

        <Box className="relative z-0 flex-1 min-h-0 flex flex-col">
          {editingMessage && (
            <Box
              className="absolute inset-0 bg-black/40 z-45"
              onClick={handleCancelEdit}
            />
          )}

          <Box className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
            {showMessagesLoader && (
              <Box className="absolute inset-0 flex items-center justify-center bg-white/60 z-30000">
                <Loader />
              </Box>
            )}

            <ChatMessageList
              messages={messages}
              discussionType={discussion?.discussionType}
              firstUnreadMessageId={firstUnreadMessageId}
              scrollMode="container"
              selectedMessageIds={
                selectedMessages.map(m => m.id).filter(Boolean) as string[]
              }
              onSelect={handleToggleSelect}
            />
          </Box>

          <Box className="sticky bottom-0 z-50">
            {editingMessage && (
              <Box className="px-2 pb-2">
                <UserMessage
                  message={editingMessage.message}
                  attachments={<MessageAttachment item={editingMessage} />}
                  time={
                    editingMessage.createdTime
                      ? formatChatTime(editingMessage.createdTime.toString())
                      : null
                  }
                  isRead={editingMessage.recipients?.some(r => r.read) ?? false}
                  isEdited={!!editingMessage.modifiedTime}
                />
              </Box>
            )}

            <ChatInput
              discussionId={discussionId}
              discussion={discussion}
              context={!discussionId ? discussionContextType : undefined}
              contextId={!discussionId ? contextId : undefined}
              setFirstUnreadMessageId={setFirstUnreadMessageId}
              onDiscussionCreated={onDiscussionCreated}
              editingMessage={editingMessage}
              onCancelEdit={handleCancelEdit}
            />
          </Box>
        </Box>
      </Stack>
    </Flex>
  );
};
